<template>
	<div v-if="isOpen" class="modal-overlay" @click="onOverlayClick">
		<div class="modal-content" @click.stop>
			<div class="modal-header">
				<h3>{{ $t("permissionPage.addPermissionModal.title") }}</h3>
				<button type="button" @click="closeModal" class="close-btn" :disabled="saving">&times;</button>
			</div>
			<div class="modal-body">
				<div v-if="submitError" class="modal-error">{{ submitError }}</div>
				<label class="field">
					<span class="field-label">{{ $t("permissionPage.addPermissionModal.name") }}</span>
					<input
						v-model="name"
						type="text"
						class="field-input"
						:placeholder="$t('permissionPage.addPermissionModal.namePlaceholder')"
						:disabled="saving"
						autocomplete="off"
					/>
				</label>
				<label class="field">
					<span class="field-label">{{ $t("permissionPage.addPermissionModal.description") }}</span>
					<textarea
						v-model="description"
						class="field-textarea"
						rows="3"
						:placeholder="$t('permissionPage.addPermissionModal.descriptionPlaceholder')"
						:disabled="saving"
					/>
				</label>
			</div>
			<div class="modal-footer">
				<Button variant="secondary" size="m" type="button" :disabled="saving" @click="closeModal">
					{{ $t("permissionPage.addPermissionModal.cancel") }}
				</Button>
				<Button
					variant="primary"
					size="m"
					type="button"
					:disabled="saving || !name.trim() || !description.trim()"
					@click="onSave"
				>
					{{ saving ? $t("permissionPage.addPermissionModal.saving") : $t("permissionPage.addPermissionModal.save") }}
				</Button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import Button from "@/components/ui/Button.vue"

interface Props {
	isOpen: boolean
	saving?: boolean
	submitError?: string
}

interface Emits {
	(e: "close"): void
	(e: "save", payload: { name: string; description: string }): void
}

const props = withDefaults(defineProps<Props>(), {
	saving: false,
	submitError: "",
})

const emit = defineEmits<Emits>()

const name = ref("")
const description = ref("")

watch(
	() => props.isOpen,
	open => {
		if (open) {
			name.value = ""
			description.value = ""
		}
	}
)

const closeModal = () => {
	if (!props.saving) {
		emit("close")
	}
}

const onOverlayClick = () => {
	closeModal()
}

const onSave = () => {
	emit("save", { name: name.value.trim(), description: description.value.trim() })
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
}

.modal-content {
	background: white;
	border-radius: 8px;
	width: 90%;
	max-width: 520px;
	max-height: 80vh;
	overflow-y: auto;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid #e0e0e0;
	background-color: #f8f9fa;

	h3 {
		margin: 0;
		color: #333;
	}

	.close-btn {
		background: none;
		border: none;
		font-size: 24px;
		cursor: pointer;
		color: #666;
		padding: 0;
		width: 30px;
		height: 30px;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover:not(:disabled) {
			color: #333;
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}
}

.modal-body {
	padding: 20px;

	.modal-error {
		color: #dc3545;
		margin-bottom: 16px;
		font-size: 0.95rem;
	}

	.field {
		display: block;
		margin-bottom: 16px;
	}

	.field-label {
		display: block;
		margin-bottom: 6px;
		font-weight: 500;
		color: #333;
		font-size: 0.9rem;
	}

	.field-input,
	.field-textarea {
		width: 100%;
		box-sizing: border-box;
		padding: 8px 12px;
		border: 1px solid #ced4da;
		border-radius: 4px;
		font-size: 1rem;
		font-family: inherit;
	}

	.field-textarea {
		resize: vertical;
		min-height: 72px;
	}

	.field-input:disabled,
	.field-textarea:disabled {
		background: #f1f3f5;
		cursor: not-allowed;
	}
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 16px 20px;
	border-top: 1px solid #e0e0e0;
	background-color: #f8f9fa;
}
</style>
